import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios, { AxiosError } from "axios";
import { Modal } from "../../components/ui/modal";
import { CheckCircle, MinusCircle, PlusCircle } from "lucide-react";
import { styles } from "../../styles/pages/Product/ProductEditStyle";
import ListboxComponent from "../../components/common/ListboxComponent";
import Loader from "../../components/common/Loader";
import { DatePicker } from "../../components/common/DatePicker";
import { format } from "date-fns";

interface FileUploadProps {
    onFileSelect: (file: File | null) => void;
    onRemove?: () => void;
    index: number;
    existingImage?: string;
    productId: string;
}

const ImagePreviewModal: React.FC<{
    isOpen: boolean;
    imageUrl: string | null;
    onClose: () => void;
}> = ({ isOpen, imageUrl, onClose }) => {
    if (!isOpen || !imageUrl) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} className={styles.imagePreviewModal}>
            <div className={styles.imagePreviewModalContent}>
                <img
                    src={imageUrl}
                    alt="Enlarged product preview"
                    className={styles.imagePreviewModalImage}
                />
            </div>
        </Modal>
    );
};

const LocalFileUpload: React.FC<FileUploadProps> = ({
    onFileSelect,
    onRemove,
    index,
    existingImage,
    productId,
}) => {
    const [, setFileName] = useState(
        existingImage ? existingImage.split("/").pop() || "Existing image" : "No file chosen"
    );
    const [previewUrl, setPreviewUrl] = useState<string | null>(existingImage || null);
    const [isImageModalOpen, setImageModalOpen] = useState(false);

    useEffect(() => {
        setFileName(
            existingImage ? existingImage.split("/").pop() || "Existing image" : "No file chosen"
        );
        setPreviewUrl(existingImage || null);
    }, [existingImage]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setFileName(
            file ? file.name : existingImage ? existingImage.split("/").pop() || "Existing image" : "No file chosen"
        );
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            setPreviewUrl(existingImage || null);
        }
        onFileSelect(file);
    };

    const handleImageClick = () => {
        if (previewUrl) {
            setImageModalOpen(true);
        }
    };

    return (
        <div className={styles.fileUploadOuterContainer}>
            <div className={styles.fileUploadInnerContainer}>
                <label
                    htmlFor={`profileFile-${index}`}
                    className={styles.fileUploadButton}
                >
                    Choose Image
                </label>
                <input
                    id={`profileFile-${index}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleChange}
                />
                {onRemove && (
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const token = sessionStorage.getItem("access_token");
                                if (!token) {
                                    alert("Authorization required. Please log in.");
                                    return;
                                }

                                if (!productId) {
                                    alert("Product ID is missing.");
                                    return;
                                }

                                const imageField = `prod_img${index + 1}`;
                                const formData = new FormData();
                                formData.append("id", productId);
                                formData.append("image_field", imageField);

                                const response = await axios.post(
                                    `${process.env.REACT_APP_API_URL}/api/product/delete-profile-image`,
                                    formData,
                                    {
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            "Content-Type": "multipart/form-data",
                                        },
                                    }
                                );

                                if (response.data.status === 200) {
                                    console.log("Image deleted successfully:", imageField);
                                    onRemove?.();
                                    setPreviewUrl(null);
                                    setFileName("No file chosen");
                                } else {
                                    console.error("Failed to delete image:", response.data.message);
                                    alert("Failed to delete image. Please try again.");
                                }
                            } catch (error) {
                                console.error("Error deleting image:", error);
                                alert("Something went wrong while deleting the image.");
                            }
                        }}
                        className={styles.fileUploadRemoveButton}
                        aria-label="Remove image"
                    >
                        <MinusCircle className="w-5 h-5" />
                    </button>
                )}
            </div>
            <div className={styles.fileUploadTextContainer}>
            </div>
            {previewUrl && (
                <div className={styles.imagePreviewContainer} onClick={handleImageClick}>
                    <img
                        src={previewUrl}
                        alt={`Product preview ${index + 1}`}
                        className={styles.imagePreview}
                    />
                    <div className={styles.imagePreviewOverlay}>
                        <span className={styles.imagePreviewText}>
                            Image {index + 1}
                        </span>
                    </div>
                </div>
            )}
            <ImagePreviewModal
                isOpen={isImageModalOpen}
                imageUrl={previewUrl}
                onClose={() => setImageModalOpen(false)}
            />
        </div>
    );
};

const ProductEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [prodName, setProdName] = useState("");
    const [prodPrice, setProdPrice] = useState("");
    const [prodDesc, setProdDesc] = useState("");
    const [prodCost, setProdCost] = useState("");
    const [prodType, setProdType] = useState("");
    const [prodUrl, setProdUrl] = useState("");
    const [prodPartName, setProdPartName] = useState("");
    const [prodPartNumber, setProdPartNumber] = useState("");
    const [prodCatId, setProdCatId] = useState("");
    const [prodSubCatId, setProdSubCatId] = useState("");
    const [prodIgst, setProdIgst] = useState("");
    const [prodSgst, setProdSgst] = useState("");
    const [prodCgst, setProdCgst] = useState("");
    const [gstType, setGstType] = useState<"IGST" | "CGST+SGST" | null>(null);
    const [prodImages, setProdImages] = useState<(File | null)[]>([]);
    const [existingImages, setExistingImages] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [categoryList, setCategoryList] = useState<any[]>([]);
    const [subCategoryList, setSubCategoryList] = useState<any[]>([]);
    const [effectiveDate, setEffectiveDate] = useState<string>("");
    const [errors, setErrors] = useState<{
        prod_name?: string;
        prod_price?: string;
        prod_desc?: string;
        prod_cost?: string;
        prod_type?: string;
        prod_url?: string;
        prod_part_name?: string;
        prod_part_number?: string;
        prod_cat_id?: string;
        prod_sub_cat_id?: string;
        prod_igst?: string;
        prod_sgst?: string;
        prod_cgst?: string;
        effective_date?: string;
        [key: `prod_img${number}`]: string | undefined;
    }>({});
    const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

    useEffect(() => {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
            setErrors({ prod_name: "Authorization required. Please log in." });
            navigate("/signin");
            setLoading(false);
            return;
        }

        const fetchProduct = async () => {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/product/edit`,
                    { id },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (response.data.status === 200 && response.data.data) {
                    const product = response.data.data;
                    setProdName(product.prod_name || "");
                    setProdPrice(product.prod_price || "");
                    setProdDesc(product.prod_desc || "");
                    setProdCost(product.prod_cost || "");
                    setProdType(product.prod_type || "");
                    setProdUrl(product.prod_url || "");
                    setProdPartName(product.prod_part_name || "");
                    setProdPartNumber(product.prod_part_number || "");
                    setProdCatId(product.prod_cat_id?.toString() || "");
                    setProdSubCatId(product.prod_sub_cat_id?.toString() || "");
                    setEffectiveDate(product.effective_date || "");
                    setProdIgst(product.IGST ? product.IGST.toString() : "");
                    setProdSgst(product.SGST ? product.SGST.toString() : "");
                    setProdCgst(product.CGST ? product.CGST.toString() : "");
                    const images = [
                        product.prod_img1 || "",
                        product.prod_img2 || "",
                        product.prod_img3 || "",
                    ];
                    setExistingImages(images);
                    setProdImages([null, null, null]);
                } else {
                    setErrors({ prod_name: "Failed to load product details." });
                }
            } catch (error: any) {
                setErrors({
                    prod_name: error.response?.data?.message || "Failed to fetch product details.",
                });
            }
        };

        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/category/name/list`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.data && Array.isArray(response.data.data)) {
                    const formattedCategories = response.data.data.map((cat: any) => ({
                        id: cat.id,
                        name: cat.category_name,
                    }));
                    setCategoryList(formattedCategories);
                }
            } catch (error: unknown) {
                console.error("Failed to fetch categories", error);
                if (error instanceof AxiosError && error.response?.status === 401) {
                    setErrors({ prod_name: "Session expired. Please log in again." });
                    navigate("/signin");
                }
            }
        };

        const fetchGstType = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/companywisegst`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.data.status === 200 && response.data.data.length > 0) {
                    const gstType = response.data.data[0].gst_type;
                    setGstType(gstType);
                } else {
                    setErrors({ prod_name: "Failed to fetch GST configuration." });
                }
            } catch (error: unknown) {
                console.error("Failed to fetch GST type", error);
                if (error instanceof AxiosError && error.response?.status === 401) {
                    navigate("/signin");
                }
                setErrors({ prod_name: "Failed to fetch GST configuration." });
            }
        };

        const fetchData = async () => {
            setLoading(true);
            await Promise.all([fetchProduct(), fetchCategories(), fetchGstType()]);
            setLoading(false);
        };

        fetchData();
    }, [id, navigate]);

    const fetchSubCategories = useCallback(async (categoryId: string) => {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
            setErrors({ prod_name: "Authorization required. Please log in." });
            navigate("/signin");
            return;
        }

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/subcategory/name/list`,
                { category_id: categoryId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.status === 200) {
                const formattedSubCategories = Array.isArray(response.data.data)
                    ? response.data.data.map((sub: any) => ({
                        id: sub.id,
                        subcategory_name: sub.subcategory_name,
                    }))
                    : [];
                setSubCategoryList(formattedSubCategories);
            } else {
                setSubCategoryList([]);
            }
        } catch (error) {
            console.error("Error fetching subcategories", error);
            setSubCategoryList([]);
        }
    }, [navigate]);

    useEffect(() => {
        if (prodCatId) {
            fetchSubCategories(prodCatId);
        } else {
            setSubCategoryList([]);
            setProdSubCatId("");
        }
    }, [prodCatId, fetchSubCategories]);

    const handleAddFileUpload = () => {
        if (prodImages.length < 3) {
            setProdImages([...prodImages, null]);
            setExistingImages([...existingImages, ""]);
        }
    };

    const handleRemoveFileUpload = (index: number) => {
        const newProdImages = [...prodImages];
        newProdImages[index] = null;
        setProdImages(newProdImages);

        const newExistingImages = [...existingImages];
        newExistingImages[index] = "";
        setExistingImages(newExistingImages);
    };

    const handleFileSelect = (index: number) => (file: File | null) => {
        const newImages = [...prodImages];
        newImages[index] = file;
        setProdImages(newImages);

        if (file) {
            const validTypes = ["image/jpeg", "image/jpg", "image/png"];
            if (validTypes.includes(file.type)) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [`prod_img${index + 1}`]: undefined,
                }));
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    [`prod_img${index + 1}`]: "The image must be a file of type: jpg, jpeg, png.",
                }));
            }
        } else {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [`prod_img${index + 1}`]: undefined,
            }));
        }
    };

    const handleNumericInput = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        setter: React.Dispatch<React.SetStateAction<string>>,
        field: string
    ) => {
        const value = e.target.value.replace(/[^0-9.]/g, "");
        setter(value);

        const numericValue = Number(value);
        const isGSTField = ["prod_igst", "prod_cgst", "prod_sgst"].includes(field);
        const isOutOfRange = isGSTField && (numericValue < 1 || numericValue > 100);

        setErrors((prev) => ({
            ...prev,
            [field]: !value
                ? `The ${field.replace("prod_", "").replace(/_/g, " ")} field is required`
                : isNaN(numericValue) || numericValue < 0
                    ? `The ${field.replace("prod_", "").replace(/_/g, " ")} must be a valid non-negative number`
                    : isOutOfRange
                        ? `The ${field.replace("prod_", "").replace(/_/g, " ")} must be between 1 and 100`
                        : undefined,
        }));
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: value.trim() ? undefined : `The ${name.replace("prod_", "").replace(/_/g, " ")} field is required`,
        }));
        switch (name) {
            case "prod_name":
                setProdName(value);
                break;
            case "prod_desc":
                setProdDesc(value);
                break;
            case "prod_type":
                setProdType(value);
                break;
            case "prod_url":
                setProdUrl(value);
                break;
            case "prod_part_name":
                setProdPartName(value);
                break;
            case "prod_part_number":
                setProdPartNumber(value);
                break;
            default:
                break;
        }
    };

    const handleDropdownChange = (field: string) => (selected: any) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: undefined }));
        if (field === "prod_cat_id") {
            setProdCatId(selected?.id || "");
            setProdSubCatId("");
            if (!selected?.id) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    prod_cat_id: "The Category field is required",
                }));
            }
        } else if (field === "prod_sub_cat_id") {
            setProdSubCatId(selected?.id || "");
        }
    };

    const handleDateChange = (selectedDates: Date[]) => {
        if (selectedDates.length > 0) {
            const formattedDate = format(selectedDates[0], "dd-MM-yyyy");
            setEffectiveDate(formattedDate);
            setErrors((prevErrors) => ({ ...prevErrors, effective_date: undefined }));
        } else {
            setEffectiveDate("");
            setErrors((prevErrors) => ({
                ...prevErrors,
                effective_date: "The Effective Date field is required",
            }));
        }
    };

    const validateForm = () => {
        const newErrors: typeof errors = {};

        if (!prodName.trim()) {
            newErrors.prod_name = "The Product name field is required";
        }

        if (!prodPrice.trim()) {
            newErrors.prod_price = "The Product price field is required";
        } else if (isNaN(Number(prodPrice)) || Number(prodPrice) <= 0) {
            newErrors.prod_price = "The Product price must be a valid positive number";
        }

        if (!prodCatId) {
            newErrors.prod_cat_id = "The Category field is required";
        }

        if (!effectiveDate) {
            newErrors.effective_date = "The Effective Date field is required";
        }

        if (!prodImages.some((img) => img !== null) && existingImages.every((img) => !img)) {
            newErrors.prod_img1 = "At least one product image is required";
        }

        if (gstType === "IGST") {
            if (!prodIgst.trim()) {
                newErrors.prod_igst = "The IGST field is required";
            } else if (
                isNaN(Number(prodIgst)) ||
                Number(prodIgst) < 1 ||
                Number(prodIgst) > 100
            ) {
                newErrors.prod_igst = "IGST must be between 1 and 100";
            }
        } else if (gstType === "CGST+SGST") {
            if (!prodSgst.trim()) {
                newErrors.prod_sgst = "The SGST field is required";
            } else if (
                isNaN(Number(prodSgst)) ||
                Number(prodSgst) < 1 ||
                Number(prodSgst) > 100
            ) {
                newErrors.prod_sgst = "SGST must be between 1 and 100";
            }

            if (!prodCgst.trim()) {
                newErrors.prod_cgst = "The CGST field is required";
            } else if (
                isNaN(Number(prodCgst)) ||
                Number(prodCgst) < 1 ||
                Number(prodCgst) > 100
            ) {
                newErrors.prod_cgst = "CGST must be between 1 and 100";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});

        if (!validateForm()) {
            setSaving(false);
            return;
        }

        const formData = new FormData();
        formData.append("id", id || "");
        formData.append("prod_name", prodName);
        formData.append("prod_price", prodPrice);
        formData.append("prod_desc", prodDesc);
        formData.append("prod_cost", prodCost);
        formData.append("prod_type", prodType);
        formData.append("prod_url", prodUrl);
        formData.append("status", "1");
        formData.append("prod_part_name", prodPartName);
        formData.append("prod_part_number", prodPartNumber);
        formData.append("prod_cat_id", prodCatId);
        formData.append("prod_sub_cat_id", prodSubCatId || "");
        formData.append("effective_date", effectiveDate);

        if (gstType === "IGST") {
            formData.append("IGST", prodIgst);
            formData.append("SGST", "0");
            formData.append("CGST", "0");
        } else if (gstType === "CGST+SGST") {
            formData.append("IGST", "0");
            formData.append("SGST", prodSgst);
            formData.append("CGST", prodCgst);
        }

        prodImages.forEach((file, index) => {
            if (file instanceof File) {
                formData.append(`prod_img${index + 1}`, file);
            }
        });

        existingImages.forEach((url, index) => {
            const correspondingProdImage = prodImages[index];
            if (!correspondingProdImage || !(correspondingProdImage instanceof File)) {
                if (url) {
                    formData.append(`existing_images[${index}]`, url);
                }
            }
        });

        const token = sessionStorage.getItem("access_token");
        if (!token) {
            setErrors({ prod_name: "Authorization required. Please log in." });
            setSaving(false);
            navigate("/signin");
            return;
        }

        try {
            if (!id) {
                setErrors({ prod_name: "Product ID is missing." });
                setSaving(false);
                return;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/product/update`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data.status === 200) {
                setSuccessModalOpen(true);
                setTimeout(() => {
                    setSuccessModalOpen(false);
                    navigate("/product-list");
                }, 2000);
            } else if (response.data.status === 422) {
                const errorData = response.data;
                setErrors(
                    Object.fromEntries(
                        Object.entries(errorData.data || errorData.errors || {}).map(([key, value]) => [
                            key,
                            Array.isArray(value) ? value[0] : value,
                        ])
                    )
                );
            } else {
                setErrors({
                    prod_name: response.data.message || "Something went wrong.",
                });
            }
        } catch (error: any) {
            const errorMessage =
                error.response?.data?.message || "Failed to update product.";
            setErrors({ prod_name: errorMessage });
            if (error.response?.status === 422) {
                setErrors(
                    Object.fromEntries(
                        Object.entries(error.response.data.data || error.response.data.errors || {}).map(([key, value]) => [
                            key,
                            Array.isArray(value) ? value[0] : value,
                        ])
                    )
                );
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Edit Product</h2>
            </div>
            {loading && <Loader />}
            {!loading && (
                <div className={styles.formContainer}>
                    <form className={styles.form} onSubmit={handleSubmit}>
                        <div className={styles.inputContainer}>
                            {/* Product Name */}
                            <div>
                                <label className={styles.label}>
                                    Name <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="prod_name"
                                    value={prodName}
                                    onChange={handleInputChange}
                                    className={`${styles.input} ${errors.prod_name ? styles.inputError : styles.inputDefault}`}
                                />
                                {errors.prod_name && (
                                    <span className={styles.errorText}>{errors.prod_name}</span>
                                )}
                            </div>

                            {/* Part Name */}
                            <div>
                                <label className={styles.label}>Part Name</label>
                                <input
                                    type="text"
                                    name="prod_part_name"
                                    value={prodPartName}
                                    onChange={handleInputChange}
                                    className={`${styles.input} ${styles.inputDefault}`}
                                />
                            </div>

                            {/* Part Number */}
                            <div>
                                <label className={styles.label}>Part Number</label>
                                <input
                                    type="text"
                                    name="prod_part_number"
                                    value={prodPartNumber}
                                    onChange={handleInputChange}
                                    className={`${styles.input} ${styles.inputDefault}`}
                                />
                            </div>

                            {/* Product Type */}
                            <div>
                                <label className={styles.label}>Type</label>
                                <input
                                    type="text"
                                    name="prod_type"
                                    value={prodType}
                                    onChange={handleInputChange}
                                    className={`${styles.input} ${styles.inputDefault}`}
                                />
                            </div>

                            {/* Product Price */}
                            <div>
                                <label className={styles.label}>
                                    Price <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="prod_price"
                                    value={prodPrice}
                                    onChange={(e) => handleNumericInput(e, setProdPrice, "prod_price")}
                                    className={`${styles.input} ${errors.prod_price ? styles.inputError : styles.inputDefault}`}
                                />
                                {errors.prod_price && (
                                    <span className={styles.errorText}>{errors.prod_price}</span>
                                )}
                            </div>

                            {/* Product Cost */}
                            <div>
                                <label className={styles.label}>Cost</label>
                                <input
                                    type="text"
                                    name="prod_cost"
                                    value={prodCost}
                                    onChange={(e) => handleNumericInput(e, setProdCost, "prod_cost")}
                                    className={`${styles.input} ${errors.prod_cost ? styles.inputError : styles.inputDefault}`}
                                />
                                {errors.prod_cost && (
                                    <span className={styles.errorText}>{errors.prod_cost}</span>
                                )}
                            </div>

                            {/* Category */}
                            <div>
                                <ListboxComponent
                                    value={categoryList.find((cat) => cat.id === Number(prodCatId)) ?? null}
                                    onChange={handleDropdownChange("prod_cat_id")}
                                    items={categoryList}
                                    label="Category"
                                    error={errors.prod_cat_id}
                                    displayKey="name"
                                    required
                                />
                            </div>

                            {/* Sub Category */}
                            <div>
                                <ListboxComponent
                                    value={subCategoryList.find((sub) => sub.id === Number(prodSubCatId)) ?? null}
                                    onChange={handleDropdownChange("prod_sub_cat_id")}
                                    items={subCategoryList}
                                    label="Sub Category"
                                    error={errors.prod_sub_cat_id}
                                    displayKey="subcategory_name"
                                    required={false}
                                />
                            </div>

                            {/* URL */}
                            <div>
                                <label className={styles.label}>URL</label>
                                <input
                                    type="text"
                                    name="prod_url"
                                    value={prodUrl}
                                    onChange={handleInputChange}
                                    className={`${styles.input} ${styles.inputDefault}`}
                                />
                            </div>

                            {/* Effective Date */}
                            <div>
                                <DatePicker
                                    value={effectiveDate}
                                    onChange={handleDateChange}
                                    label="Effective Date"
                                    error={errors.effective_date}
                                    required
                                    className={styles.datePickerContainer}
                                />
                            </div>

                            {/* IGST */}
                            {gstType === "IGST" && (
                                <div>
                                    <label className={styles.label}>
                                        IGST <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="prod_igst"
                                        value={prodIgst}
                                        inputMode="decimal"
                                        maxLength={3}
                                        onChange={(e) => handleNumericInput(e, setProdIgst, "prod_igst")}
                                        className={`${styles.input} ${errors.prod_igst ? styles.inputError : styles.inputDefault}`}
                                    />
                                    {errors.prod_igst && (
                                        <span className={styles.errorText}>{errors.prod_igst}</span>
                                    )}
                                </div>
                            )}

                            {/* CGST + SGST */}
                            {gstType === "CGST+SGST" && (
                                <div className="col-span-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={styles.label}>
                                            CGST <span className={styles.required}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="prod_cgst"
                                            value={prodCgst}
                                            inputMode="decimal"
                                            maxLength={3}
                                            onChange={(e) => handleNumericInput(e, setProdCgst, "prod_cgst")}
                                            className={`${styles.input} ${errors.prod_cgst ? styles.inputError : styles.inputDefault}`}
                                        />
                                        {errors.prod_cgst && (
                                            <span className={styles.errorText}>{errors.prod_cgst}</span>
                                        )}
                                    </div>
                                    <div>
                                        <label className={styles.label}>
                                            SGST <span className={styles.required}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="prod_sgst"
                                            value={prodSgst}
                                            inputMode="decimal"
                                            maxLength={3}
                                            onChange={(e) => handleNumericInput(e, setProdSgst, "prod_sgst")}
                                            className={`${styles.input} ${errors.prod_sgst ? styles.inputError : styles.inputDefault}`}
                                        />
                                        {errors.prod_sgst && (
                                            <span className={styles.errorText}>{errors.prod_sgst}</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Description */}
                            <div className="col-span-1 md:col-span-2">
                                <label className={styles.label}>Description</label>
                                <textarea
                                    name="prod_desc"
                                    value={prodDesc}
                                    onChange={handleInputChange}
                                    className={`${styles.input} min-h-[100px]`}
                                />
                            </div>

                            {/* Product Image Section */}
                            <div className="col-span-1 md:col-span-3 space-y-3">
                                <label className={styles.label}>
                                    Product Image <span className={styles.required}>*</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {prodImages.map((_, index) => (
                                        <div key={index}>
                                            <LocalFileUpload
                                                index={index}
                                                onFileSelect={handleFileSelect(index)}
                                                onRemove={index > 0 ? () => handleRemoveFileUpload(index) : undefined}
                                                existingImage={existingImages[index]}
                                                productId={id || ""}
                                            />
                                            {errors[`prod_img${index + 1}`] && (
                                                <span className={styles.errorText}>{errors[`prod_img${index + 1}`]}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                {prodImages.length < 3 && (
                                    <button
                                        type="button"
                                        onClick={handleAddFileUpload}
                                        className={styles.addFileButton}
                                    >
                                        <PlusCircle className="w-5 h-5" />
                                        <span>Add Another Image</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className={styles.buttonsContainer}>
                            <button
                                className={styles.submitButton}
                                type="submit"
                                disabled={saving || loading}
                            >
                                {saving ? "Saving..." : "Save"}
                            </button>
                            <button
                                className={styles.cancelButton}
                                type="button"
                                onClick={() => navigate("/product-list")}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <Modal
                isOpen={isSuccessModalOpen}
                onClose={() => setSuccessModalOpen(false)}
                className={styles.modal}
            >
                <button
                    onClick={() => setSuccessModalOpen(false)}
                    className={styles.modalCloseButton}
                ></button>
                <div className={styles.modalIconContainer}>
                    <CheckCircle className="w-6 h-6" />
                </div>
                <h4 className={styles.modalTitle}>Product Updated Successfully</h4>
                <p className={styles.modalText}>
                    The product has been updated in the system successfully.
                </p>
                <div className={styles.modalButtonContainer}>
                    <button
                        onClick={() => setSuccessModalOpen(false)}
                        className={styles.modalButton}
                    >
                        OK
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default ProductEdit;
